import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";

// PII patterns we never want to leave the browser.
const EMAIL_RE = /[\w.-]+@[\w.-]+/;
const PHONE_RE = /\+?\d[\d\s().-]{7,}/;

function looksLikePII(value: unknown): boolean {
  if (typeof value !== "string") return false;
  return EMAIL_RE.test(value) || PHONE_RE.test(value);
}

function scrubMap<T extends Record<string, unknown> | undefined>(map: T): T {
  if (!map) return map;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(map)) {
    if (looksLikePII(v)) continue; // drop the entry entirely
    out[k] = v;
  }
  return out as T;
}

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Init Sentry only when a DSN is configured.
// The app must not crash if VITE_SENTRY_DSN is missing — Sentry simply doesn't init.
if (dsn) {
  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    beforeSend(event) {
      // 1. Drop request cookies — they can carry session tokens.
      if (event.request) {
        delete event.request.cookies;
      }

      // 2. Strip PII from extra/tags (drop entries whose value looks like
      //    an email or phone number).
      event.extra = scrubMap(event.extra);
      event.tags = scrubMap(event.tags);

      // 3. Drop breadcrumbs whose URL touches Supabase auth (token-bearing).
      if (Array.isArray(event.breadcrumbs)) {
        event.breadcrumbs = event.breadcrumbs.filter((b) => {
          const url =
            (b.data && (b.data.url || b.data.to)) ||
            (typeof b.message === "string" ? b.message : undefined);
          if (typeof url === "string" && url.includes("/auth/v1/")) {
            return false;
          }
          return true;
        });
      }

      // 4. Drop user fields except a hashed identifier if one is provided.
      if (event.user) {
        const id = typeof event.user.id === "string" ? event.user.id : undefined;
        // Treat as already-hashed iff it looks like a hex/base64 hash and is
        // not an email/phone. Otherwise drop the user object entirely.
        const looksHashed =
          !!id && !looksLikePII(id) && /^[A-Za-z0-9+/=_-]{16,}$/.test(id);
        event.user = looksHashed ? { id } : undefined;
      }

      return event;
    },
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
